import { z } from 'zod';
import { ACCOUNT_UI_EVENTS, ANONYMOUS_UI_EVENTS } from '../ui-usage.events.js';
// =============================================================================
// UI usage events (POST /accounts/:accountId/ui-usage-events)
// =============================================================================
/**
 * One UI usage event as the client sends it: a registry name and its payload. A union
 * discriminated on `name`, derived from `ACCOUNT_UI_EVENTS`, so the payload is validated
 * against exactly that event's closed shape and a new registry entry is publishable with no
 * second edit. The types below live here, not in `ui-usage.events.ts`, because
 * `Extract<E, { name: N }>` names a `name` property, which the PII lint refuses in an events file.
 */
export const UiUsageEventSchema = z.discriminatedUnion('name', ACCOUNT_UI_EVENTS);
export const UI_USAGE_EVENT_NAMES = ACCOUNT_UI_EVENTS.map((event) => event.shape.name.value);
/** The most events one request may carry; one invalid event refuses the whole batch. */
export const UI_USAGE_EVENTS_MAX_BATCH = 25;
export const RecordUiUsageEventsRequestSchema = z.strictObject({
    events: z.array(UiUsageEventSchema).min(1).max(UI_USAGE_EVENTS_MAX_BATCH),
});
export const RecordUiUsageEventsResponseSchema = z.object({
    /** How many events were published. */
    recorded: z.number().int().min(0),
});
// =============================================================================
// Anonymous UI usage events (POST /ui-usage-events/anonymous)
// =============================================================================
/**
 * One anonymous UI usage event, derived from `ANONYMOUS_UI_EVENTS` the way `UiUsageEvent` is
 * from the account registry. The two unions are disjoint by name, so each route's strict body
 * schema refuses the other channel's events.
 */
export const AnonymousUiUsageEventSchema = z.discriminatedUnion('name', ANONYMOUS_UI_EVENTS);
export const ANONYMOUS_UI_USAGE_EVENT_NAMES = ANONYMOUS_UI_EVENTS.map((event) => event.shape.name.value);
/** The anonymous ingest path, shared by the route, the app client and the landing site so a rename breaks every caller's build. */
export const ANONYMOUS_UI_USAGE_EVENTS_PATH = '/ui-usage-events/anonymous';
/** One event per request: the anonymous channel is never batched, so no shared request links two steps. */
export const RecordAnonymousUiUsageEventRequestSchema = z.strictObject({ event: AnonymousUiUsageEventSchema });
